import React from "react";
import { Control, useFieldArray, Controller } from "react-hook-form";
import { Box, Button, Input, Text, Alert } from "rizzui";
import { PiInfoBold, PiPlusBold } from "react-icons/pi";
import { FormData } from "../types";

interface SSDSectionProps {
  control: Control<FormData>;
}

const SSDSection: React.FC<SSDSectionProps> = ({ control }) => {
  const { fields: ssdBefore2025Fields, append: ssdBefore2025Append } =
    useFieldArray({
      control,
      name: "ssdRates.before2025",
    });

  const { fields: ssdFrom2025Fields, append: ssdFrom2025Append } =
    useFieldArray({
      control,
      name: "ssdRates.from2025",
    });

  const { fields: ssdCommercialFields, append: ssdCommercialAppend } =
    useFieldArray({
      control,
      name: "ssdRates.commercialIndustrial",
    });

  return (
    <Box>
      <div className="pb-6 md:p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Seller Stamp Duty (SSD) Rates</h3>
        <Text className="text-sm text-gray-600 mt-1">
          Configure SSD rates for residential and commercial properties
        </Text>
      </div>
      <div className="pt-6 md:p-6 space-y-6">
        {/* Residential Before 2025 */}
        <div>
          <Text className="font-medium mb-3">
            Residential Properties (Before 2025)
          </Text>
          <div className="border rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {ssdBefore2025Fields.map((field, index) => (
                <div key={field.id} className="text-left">
                  <Text className="text-sm font-medium mb-1">
                    Years {field.minYears}-
                    {field.maxYears === 99 ? "+" : field.maxYears} (%)
                  </Text>
                  <Controller
                    name={`ssdRates.before2025.${index}.rate`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="text-center"
                        min="0"
                        max="100"
                      />
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Residential From 2025 */}
        <div>
          <Text className="font-medium mb-3">
            Residential Properties (From 2025)
          </Text>
          <div className="border rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {ssdFrom2025Fields.map((field, index) => (
                <div key={field.id} className="text-left">
                  <Text className="text-sm font-medium mb-1">
                    Years {field.minYears}-
                    {field.maxYears === 99 ? "+" : field.maxYears} (%)
                  </Text>

                  <Controller
                    name={`ssdRates.from2025.${index}.rate`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="text-center"
                        min="0"
                        max="100"
                      />
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Commercial/Industrial */}
        <div>
          <Text className="font-medium mb-3">
            Commercial/Industrial Properties
          </Text>
          <div className="border rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {ssdCommercialFields.map((field, index) => (
                <div key={field.id} className="text-left">
                  <Text className="text-sm font-medium mb-1">
                    Years {field.minYears}-
                    {field.maxYears === 99 ? "+" : field.maxYears} (%)
                  </Text>

                  <Controller
                    name={`ssdRates.commercialIndustrial.${index}.rate`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="text-center"
                        min="0"
                        max="100"
                      />
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default SSDSection;
